import React, { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import {
  INVOICE_STATUS_LABELS,
  Invoice,
  InvoiceDetailResponse,
  InvoiceHistoryEntry,
  InvoiceStatus,
  invoicesAPI,
} from '@/services/api/invoices';
import { AgingBadge, InvoiceStatusPill } from './InvoiceBadges';

interface InvoiceDetailPanelProps {
  /** Invoice id to load. When null the panel is closed. */
  invoiceId: string | null;
  onClose: () => void;
  onAction?: (action: DetailAction, invoice: Invoice) => void;
}

/**
 * Action keys forwarded to the parent so the existing modals (Receive,
 * Submit, Dispute, Resolve, Resubmit, Reopen, Exception) keep being the
 * single source of truth for transitions.
 */
export type DetailAction =
  | 'receive'
  | 'submit'
  | 'dispute'
  | 'resolve'
  | 'resubmit'
  | 'reopen'
  | 'exception';

// ─────────────────────────────────────────────────────────────────────────
// Formatters

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * "Apr 23, 2:44pm" — drop year if same as current, drop seconds, lowercase
 * am/pm with no space. The previous "Apr 23, 2026, 02:44 PM" was loud and
 * dominated the actor line.
 */
function formatActivityTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const timeStr = d
    .toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase()
    .replace(/\s+/g, '');
  return `${dateStr} · ${timeStr}`;
}

/**
 * Cleanly compose actor display. The API sometimes returns numeric-only
 * "names" (user ids) — fall back to channel/role in that case so the line
 * doesn't read like a leaked db column.
 */
function formatActor(by: { name?: string; role?: string }): string {
  const name = by.name?.trim() ?? '';
  const role = by.role?.toLowerCase().trim() ?? '';
  const isNumericId = name && /^\d+$/.test(name);
  if (!name && !role) return 'System';
  if (!name || isNumericId) return role || 'System';
  if (!role) return name;
  return `${name} (${role})`;
}

// Color mapping for the timeline node dot. Keeps the panel decoupled from
// the (intentionally domain-aware) STATUS_TONE in InvoiceBadges.
const TRANSITION_DOT: Record<string, string> = {
  PENDING_COLLECTION: 'bg-(--state-warning-base)',
  RECEIVED_AT_HUB: 'bg-(--state-success-base)',
  WAITING_CONFIRMATION: 'bg-(--state-verified-base)',
  DISPUTED: 'bg-(--state-highlighted-base)',
  RESOLVED: 'bg-(--state-success-base)',
  EXCEPTION: 'bg-(--state-error-base)',
  COLLECTED: 'bg-(--state-information-base)',
  SUBMITTED_TO_CLIENT: 'bg-(--state-verified-base)',
  CLIENT_ACKNOWLEDGED: 'bg-(--state-success-base)',
};

// ─────────────────────────────────────────────────────────────────────────
// Panel

const InvoiceDetailPanel: React.FC<InvoiceDetailPanelProps> = ({
  invoiceId,
  onClose,
  onAction,
}) => {
  const [data, setData] = useState<InvoiceDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    invoicesAPI
      .detail(invoiceId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load invoice detail'
        );
        toast.error('Could not load invoice detail', {
          description: err instanceof Error ? err.message : '',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  const open = invoiceId !== null;

  const titleNode = data ? (
    <span className="flex flex-col gap-2">
      <span className="flex items-center gap-2 flex-wrap">
        <InvoiceStatusPill status={data.invoice.status} />
        {data.invoice.agingTier && data.invoice.agingTier !== 'green' && (
          <AgingBadge
            tier={data.invoice.agingTier}
            daysSince={data.invoice.daysSince}
          />
        )}
      </span>
      <span className="font-mono text-sm font-semibold text-text-strong-950 tabular-nums">
        {data.invoice.invoiceNumber}
      </span>
    </span>
  ) : loading ? (
    'Loading invoice…'
  ) : (
    'Invoice'
  );

  const footer =
    data && onAction ? (
      <FooterActions
        invoice={data.invoice}
        onAction={(action) => onAction(action, data.invoice)}
      />
    ) : undefined;

  return (
    <FormModal
      open={open}
      onClose={onClose}
      position="right"
      size="md"
      padded={false}
      title={titleNode}
      subtitle={
        data ? (
          <span className="font-mono text-[11px] tabular-nums">
            Booking ID · {data.invoice.bookingId}
          </span>
        ) : undefined
      }
      footer={footer}
    >
      <div className="px-5 py-5">
        {loading && !data && <DetailSkeleton />}
        {error && !loading && (
          <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
            {error}
          </div>
        )}
        {data && (
          <div className="space-y-7">
            <DetailsSection invoice={data.invoice} />
            <ActivitySection history={data.history} />
          </div>
        )}
      </div>
    </FormModal>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Details — single section, hairline rows, no eyebrow noise

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h4 className="mb-3 text-sm font-bold tracking-tight text-text-strong-950">
    {children}
  </h4>
);

interface RowProps {
  label: string;
  /** Primary value. Skip the row entirely when null/undefined/empty string. */
  value: React.ReactNode;
  /** Optional muted sub-line under the value (e.g. address). */
  sub?: React.ReactNode;
  /** Render value with mono font + tabular nums (codes, ids). */
  mono?: boolean;
  /** Optional inline tag chip beside the value (e.g. "Drop-in"). */
  tag?: React.ReactNode;
}

const Row: React.FC<RowProps> = ({ label, value, sub, mono, tag }) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <dt className="text-[12px] text-text-sub-600 shrink-0 pt-0.5">{label}</dt>
    <dd className="text-right min-w-0">
      <div className="flex items-center justify-end gap-2">
        {tag}
        <span
          className={`text-[13px] text-text-strong-950 tracking-tight ${
            mono ? 'font-mono tabular-nums' : ''
          }`}
        >
          {value}
        </span>
      </div>
      {sub && (
        <div className="mt-0.5 text-[11px] text-text-sub-600 leading-snug">
          {sub}
        </div>
      )}
    </dd>
  </div>
);

const DetailsSection: React.FC<{ invoice: Invoice }> = ({ invoice }) => {
  const isDropIn = invoice.outlet && !invoice.outlet.id;

  return (
    <section>
      <SectionTitle>Details</SectionTitle>
      <dl className="divide-y divide-stroke-soft-200 border-y border-stroke-soft-200">
        <Row label="Client" value={invoice.client.name} />
        {invoice.outlet && (
          <Row
            label="Recipient"
            value={invoice.outlet.name}
            sub={invoice.outlet.address}
            tag={
              isDropIn ? (
                <Badge status="neutral" size="sm">
                  Drop-in
                </Badge>
              ) : undefined
            }
          />
        )}
        {invoice.hub && (
          <Row label="Hub" value={invoice.hub.name} sub={invoice.hub.address} />
        )}
        {invoice.rider?.name && (
          <Row label="Rider" value={invoice.rider.name} />
        )}
        <Row label="Issue date" value={formatDate(invoice.issueDate)} />
        {invoice.dueDate && (
          <Row label="Due date" value={formatDate(invoice.dueDate)} />
        )}
        <Row label="Items" value={invoice.itemsCount} mono />
        {invoice.batchId && <Row label="Batch" value={invoice.batchId} mono />}
        {invoice.notes && (
          <div className="py-2.5">
            <dt className="text-[12px] text-text-sub-600 mb-1.5">Notes</dt>
            <dd className="whitespace-pre-wrap text-[13px] text-text-sub-600 leading-relaxed bg-bg-weak-50 border border-stroke-soft-200 rounded-md px-3 py-2">
              {invoice.notes}
            </dd>
          </div>
        )}
        {invoice.exceptionEvidence?.length ? (
          <div className="py-2.5">
            <dt className="text-[12px] text-text-sub-600 mb-1.5">
              Exception evidence
            </dt>
            <dd>
              <TimelineImages
                images={invoice.exceptionEvidence.map((img) => ({
                  imageId: img.imageId,
                  imageUrl: img.imageUrl,
                  caption: formatActivityTime(img.capturedAt),
                }))}
              />
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Timeline images — thumbnails attached to a status transition, click to
// open a lightbox with prev/next nav (matches the delivery detail proof view).

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  FAKTUR: 'Faktur',
  TANDA_TERIMA_PESANAN: 'Tanda terima pesanan',
};

function documentTypeLabel(type: string): string {
  return (
    DOCUMENT_TYPE_LABELS[type] ??
    type
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
  );
}

type TimelineImage = { imageId: string; imageUrl: string; caption: string };

const TimelineImages: React.FC<{ images: TimelineImage[] }> = ({ images }) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  useEffect(() => {
    if (openIdx === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenIdx(null);
      if (e.key === 'ArrowLeft')
        setOpenIdx((i) => (i !== null && i > 0 ? i - 1 : i));
      if (e.key === 'ArrowRight')
        setOpenIdx((i) => (i !== null && i < images.length - 1 ? i + 1 : i));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openIdx, images.length]);

  if (images.length === 0) return null;

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {images.map((img, i) => (
          <button
            key={img.imageId}
            type="button"
            onClick={() => setOpenIdx(i)}
            className="group relative h-16 w-16 overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-weak-50 transition-colors hover:border-accent"
            aria-label={`Preview ${img.caption}`}
            title={img.caption}
          >
            <img
              src={img.imageUrl}
              alt={img.caption}
              className="h-full w-full cursor-zoom-in object-cover"
            />
            <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em] text-white">
              {img.caption}
            </span>
          </button>
        ))}
      </div>

      {openIdx !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setOpenIdx(null)}
        >
          <div className="relative flex max-h-[88vh] max-w-[90vw] flex-col items-center">
            <img
              src={images[openIdx].imageUrl}
              alt={images[openIdx].caption}
              className="max-h-[82vh] max-w-[90vw] rounded-2xl object-contain shadow-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white/90">
              {images[openIdx].caption}
              <span className="ml-2 font-mono tabular-nums text-white/60">
                {openIdx + 1} / {images.length}
              </span>
            </p>
          </div>
          {openIdx > 0 && (
            <button
              type="button"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                setOpenIdx((i) => (i ?? 0) - 1);
              }}
              className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}
          {openIdx < images.length - 1 && (
            <button
              type="button"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                setOpenIdx((i) => (i ?? 0) + 1);
              }}
              className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          )}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Activity — single reverse-chrono timeline

const ActivitySection: React.FC<{ history: InvoiceHistoryEntry[] }> = ({
  history,
}) => {
  const reversed = useMemo(() => [...history].reverse(), [history]);

  if (reversed.length === 0) {
    return (
      <section>
        <SectionTitle>Activity</SectionTitle>
        <div className="rounded-lg border border-dashed border-stroke-soft-200 px-4 py-8 text-center">
          <i className="ri-time-line text-2xl text-text-soft-400" />
          <p className="mt-2 text-xs text-text-sub-600">
            Nothing has happened to this invoice yet.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SectionTitle>Activity</SectionTitle>
      <ol className="space-y-0">
        {reversed.map((row, idx) => {
          const isLast = idx === reversed.length - 1;
          const fromLabel = row.fromStatus
            ? (INVOICE_STATUS_LABELS[row.fromStatus as InvoiceStatus] ??
              row.fromStatus)
            : 'Created';
          const toLabel =
            INVOICE_STATUS_LABELS[row.toStatus as InvoiceStatus] ??
            row.toStatus;
          const dot = TRANSITION_DOT[row.toStatus] ?? 'bg-text-soft-400';
          // Strip the [REASON_CODE] prefix that Dispute / Exception modals
          // store at the start of the note — it's already implied by the
          // transition itself.
          const cleanNote = row.note?.replace(/^\[[A-Z_]+\]\s*/, '');

          return (
            <li key={row.id} className="relative flex gap-3">
              {/* Timeline rail: dot + connector line */}
              <div className="flex flex-col items-center pt-1">
                <span className={`h-2 w-2 rounded-full ${dot}`} />
                {!isLast && (
                  <span className="flex-1 w-px bg-bg-soft-200 mt-1" />
                )}
              </div>

              <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-4'}`}>
                {/* Transition line — fromStatus muted, arrow, toStatus emphasized */}
                <p className="text-[13px] tracking-tight">
                  <span className="text-text-sub-600">{fromLabel}</span>
                  <i className="ri-arrow-right-line mx-1.5 text-text-soft-400 text-[12px]" />
                  <span className="font-semibold text-text-strong-950">
                    {toLabel}
                  </span>
                </p>
                {/* Actor + time — quiet, mono for the timestamp */}
                <p className="mt-0.5 text-[11px] text-text-sub-600">
                  <span className="capitalize">
                    {formatActor(row.changedBy)}
                  </span>
                  <span className="text-text-soft-400 mx-1.5">·</span>
                  <span className="font-mono tabular-nums">
                    {formatActivityTime(row.changedAt)}
                  </span>
                </p>
                {cleanNote && (
                  <p className="mt-1.5 whitespace-pre-wrap text-[12px] text-text-sub-600 leading-relaxed bg-bg-weak-50 border border-stroke-soft-200 rounded-md px-2.5 py-1.5">
                    {cleanNote}
                  </p>
                )}
                {row.invoiceImages?.images?.length ? (
                  <TimelineImages
                    images={row.invoiceImages.images.map((img) => ({
                      imageId: img.imageId,
                      imageUrl: img.imageUrl,
                      caption: documentTypeLabel(img.documentType),
                    }))}
                  />
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Footer

const FooterActions: React.FC<{
  invoice: Invoice;
  onAction: (action: DetailAction) => void;
}> = ({ invoice, onAction }) => {
  // Mirror the row-level action buttons from the board, gated by current status.
  const actions: { key: DetailAction; label: string; primary?: boolean }[] = [];
  if (
    invoice.status === 'PENDING_COLLECTION' ||
    invoice.status === 'RECEIVED_BY_RIDER'
  ) {
    actions.push({ key: 'receive', label: 'Mark received', primary: true });
    if (invoice.status === 'RECEIVED_BY_RIDER') {
      actions.push({ key: 'submit', label: 'Submit for confirm' });
    }
    actions.push({ key: 'exception', label: 'Flag exception' });
  } else if (invoice.status === 'RECEIVED_AT_HUB') {
    actions.push({ key: 'submit', label: 'Submit for confirm', primary: true });
    actions.push({ key: 'exception', label: 'Flag exception' });
  } else if (invoice.status === 'WAITING_CONFIRMATION') {
    actions.push({ key: 'resolve', label: 'Resolve', primary: true });
    actions.push({ key: 'dispute', label: 'Dispute' });
  } else if (invoice.status === 'DISPUTED') {
    actions.push({ key: 'resubmit', label: 'Resubmit', primary: true });
  } else if (invoice.status === 'EXCEPTION') {
    actions.push({ key: 'reopen', label: 'Reopen' });
  }

  if (actions.length === 0) return null;

  // Renders inside SideBarModal's footer slot, which already provides
  // bg-bg-weak-50 + border-top + padding. Just lay out the buttons.
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {actions.map((a) => (
        <Button
          key={a.key}
          tone={a.primary ? 'primary' : 'neutral'}
          style={a.primary ? 'filled' : 'stroke'}
          onClick={() => onAction(a.key)}
        >
          {a.label}
        </Button>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Skeleton — matches the new structure (header + 1 details block + 1 activity block)

const DetailSkeleton: React.FC = () => (
  <div className="space-y-7 animate-pulse">
    <div className="space-y-2">
      <div className="h-4 w-24 rounded-md bg-bg-soft-200" />
      <div className="h-3 w-40 rounded-md bg-bg-weak-50" />
    </div>
    <div>
      <div className="h-3 w-16 rounded-md bg-bg-soft-200 mb-3" />
      <div className="space-y-2 border-y border-stroke-soft-200 py-2">
        <div className="flex justify-between gap-4">
          <div className="h-3 w-16 rounded-md bg-bg-weak-50" />
          <div className="h-3 w-32 rounded-md bg-bg-weak-50" />
        </div>
        <div className="flex justify-between gap-4">
          <div className="h-3 w-20 rounded-md bg-bg-weak-50" />
          <div className="h-3 w-40 rounded-md bg-bg-weak-50" />
        </div>
        <div className="flex justify-between gap-4">
          <div className="h-3 w-12 rounded-md bg-bg-weak-50" />
          <div className="h-3 w-24 rounded-md bg-bg-weak-50" />
        </div>
      </div>
    </div>
    <div>
      <div className="h-3 w-20 rounded-md bg-bg-soft-200 mb-3" />
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="h-2 w-2 rounded-full bg-bg-soft-200 mt-1.5" />
          <div className="flex-1 space-y-1">
            <div className="h-3 w-3/4 rounded-md bg-bg-weak-50" />
            <div className="h-3 w-1/2 rounded-md bg-bg-weak-50" />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="h-2 w-2 rounded-full bg-bg-soft-200 mt-1.5" />
          <div className="flex-1 space-y-1">
            <div className="h-3 w-2/3 rounded-md bg-bg-weak-50" />
            <div className="h-3 w-1/2 rounded-md bg-bg-weak-50" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default InvoiceDetailPanel;
